import React, { FC } from 'react';
import {
  Box,
  createStyles,
  Divider,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { BlogArticleFragment } from '../../../../__generated__/gatsby-types';
import colors from '../../../../themes/main-colors';
import { Link } from 'gatsby';
import { usePathContext } from '../../../../providers/PathProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    panesContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        minHeight: '200px',
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: '253px',
      },
    },
    pane: {
      height: '100%',
      display: 'flex',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column-reverse',
      },
    },
    paneImgContainer: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('sm')]: {
        padding: '0px 10px',
      },
    },
    paneImg: {
      width: '200px',
      height: '200px',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '410px',
      },
      objectFit: ({ item }: Props) =>
        `${
          item?.imageFocusHorizontal || item?.imageFocusVertical
            ? 'cover'
            : 'fill'
        }`,
      objectPosition: ({ item }: Props) =>
        `${item?.imageFocusHorizontal || '50%'} ${item?.imageFocusVertical ||
          '50%'}`,
    },
    paneDetailsContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      paddingRight: '30px',
      [theme.breakpoints.down('sm')]: {
        marginLeft: 'unset',
        height: 'inherit',
        padding: '20px',
      },
    },
    paneHeader: {
      fontSize: '14px',
      fontWeight: 'bold',
      marginBottom: '10px',
      textTransform: 'uppercase',
    },
    articlePublishDate: {
      fontSize: '14px',
      whiteSpace: 'nowrap',
    },
    articleSlug: {
      fontSize: '14px',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      textDecoration: 'underline',
    },
    teaserText: {
      maxWidth: '100%',
      fontSize: '18px',
      lineHeight: theme.typography.pxToRem(22.48),
      maxHeight: theme.typography.pxToRem(103), // For truncation. Adjust based on lineHeight & # of lines
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 4,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      paddingBottom: theme.spacing(1.25),
      paddingTop: theme.spacing(1.25),
    },
    paneSubHeader: {
      lineHeight: theme.typography.pxToRem(24),
      fontSize: theme.spacing(2.5),
      textDecoration: 'underline',
    },
    divider: {
      width: '1px',
      height: '23px',
      backgroundColor: '#D7D7D7',
      margin: '0 8px',
    },
  }),
);

export const getBackgroundColor = (color: any | null): any | null => {
  let bgColor = colors.white;
  switch (color) {
    case 'Blue':
      bgColor = colors.riverBlue;
      break;
    case 'Grey':
      bgColor = colors.cloudGrey2;
      break;
    case 'Linen-White':
      bgColor = colors.linenWhite;
      break;
    case 'Yellow':
      bgColor = colors.lightYellow;
      break;
    default:
      bgColor = colors.white;
      break;
  }
  return bgColor;
};

interface Props {
  item: BlogArticleFragment;
  style?: React.CSSProperties | undefined;
}

const ArticleCard: FC<Props> = (props: Props) => {
  const { item } = props;
  const { resolve } = usePathContext();
  const classes = useStyles(props);

  return (
    <Box className={classes.panesContainer}>
      <Box className={classes.pane}>
        <Box className={classes.paneDetailsContainer}>
          {item?.category?.name && (
            <Typography className={classes.paneHeader}>
              {item?.category?.name}
            </Typography>
          )}
          {item?.title && (
            <Link to={resolve(item?.slug)} className={classes.paneSubHeader}>
              {item?.title}
            </Link>
          )}
          {item?.teaserText && (
            <Typography className={classes.teaserText}>
              {item?.teaserText}
            </Typography>
          )}
          <Box display="flex" alignItems="center">
            {item?.formattedPublishDate && (
              <Typography className={classes.articlePublishDate}>
                {item?.formattedPublishDate}
              </Typography>
            )}
            {item?.author?.name && (
              <>
                <Divider className={classes.divider} />
                <Link
                  to={resolve(item?.author?.slug)}
                  className={classes.articleSlug}
                >
                  {item?.author?.name}
                </Link>
              </>
            )}
          </Box>
        </Box>
        <Box className={classes.paneImgContainer}>
          <img
            alt=""
            src={item?.heroImage?.file?.url}
            className={classes.paneImg}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ArticleCard;
